import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Calendar,
  Users,
  UserCircle,
  Clipboard,
  LogOut,
  Bell,
  Building2,
  Hospital,
  MessageSquare,
  FileUp,
  CalendarOff,
  Clock,
  ShieldCheck,
  PhoneCall,
  Receipt,
  LucideIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

declare const __APP_VERSION__: string;

type MenuItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  /**
   * Permission exigida (resource, action). Se omitido, todos os autenticados veem.
   * Menu filtra via AuthContext.can().
   */
  requires?: [string, string];
};

const menuGroups: { label: string; items: MenuItem[] }[] = [
  {
    label: "Operação diária",
    items: [
      { title: "Início", url: "/home", icon: Home },
      { title: "Agenda", url: "/schedule", icon: Calendar, requires: ["appointments", "read"] },
      { title: "Central de Mensagens", url: "/chat-observer", icon: MessageSquare, requires: ["chat_tickets", "read"] },
      { title: "Central de Notificações", url: "/notifications", icon: Bell, requires: ["notifications", "read"] },
      { title: "Atendimentos", url: "/appointments", icon: Clipboard, requires: ["appointments", "read"] },
      { title: "Autorizações", url: "/insurance-authorizations", icon: ShieldCheck, requires: ["insurance_authorizations", "read"] },
      { title: "Follow-Ups", url: "/follow-ups", icon: PhoneCall, requires: ["follow_ups", "read"] },
    ],
  },
  {
    label: "Faturamento",
    items: [
      { title: "Convênios", url: "/tiss-billing", icon: Receipt, requires: ["insurance_claims", "read"] },
    ],
  },
  {
    label: "Cadastros",
    items: [
      { title: "Pacientes", url: "/patients", icon: Users, requires: ["patients", "read"] },
      { title: "Profissionais", url: "/dentists", icon: UserCircle, requires: ["dentists", "read"] },
      { title: "Expedientes", url: "/schedule-setup", icon: Clock, requires: ["schedule_setups", "read"] },
      { title: "Feriados", url: "/holidays", icon: CalendarOff, requires: ["holidays", "read"] },
    ],
  },
  {
    label: "Administração",
    items: [
      { title: "Importar Atendimentos", url: "/import-appointments", icon: FileUp, requires: ["appointments", "import_bulk"] },
      { title: "Clínicas", url: "/clinics-detail", icon: Hospital, requires: ["clinics", "read"] },
      { title: "Empresas", url: "/companies", icon: Building2, requires: ["companies", "read"] },
      { title: "Usuários", url: "/users", icon: Users, requires: ["users", "read"] },
    ],
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const currentPath = location.pathname;
  const isActive = (path: string) => currentPath === path;
  const collapsed = state === "collapsed";

  const renderItem = (item: MenuItem) => {
    const active = isActive(item.url);
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          onClick={() => navigate(item.url)}
          title={collapsed ? item.title : undefined}
          className={cn(
            "relative transition-colors",
            active
              ? "bg-primary/10 text-primary font-medium hover:bg-primary/15 hover:text-primary"
              : "text-foreground/80 hover:bg-muted hover:text-foreground"
          )}
        >
          {active && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] bg-primary rounded-r" />
          )}
          <item.icon className={cn("h-4 w-4", active && "text-primary")} />
          {!collapsed && <span>{item.title}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className={collapsed ? "w-16" : "w-64"} collapsible="icon">
      <SidebarHeader className="border-b border-border flex flex-row items-center h-14 px-3 gap-2">
        <SidebarTrigger className="w-8 h-8 text-primary shrink-0" />
        {!collapsed && (
          <div className="flex items-baseline gap-1 truncate">
            <span className="font-sans text-xs font-semibold tracking-tight text-foreground">odonto</span>
            <span className="font-display text-sm italic text-accent">inclusiva</span>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {menuGroups.map((group) => {
          const visibleItems = group.items.filter(
            (i) => !i.requires || can(i.requires[0], i.requires[1])
          );
          if (visibleItems.length === 0) return null;
          return (
            <SidebarGroup key={group.label}>
              {!collapsed && (
                <SidebarGroupLabel className="font-display text-[10px] italic uppercase tracking-[0.18em] text-muted-foreground/80 px-3">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map(renderItem)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => navigate("/login")}
              title={collapsed ? "Sair do Sistema" : undefined}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Sair do Sistema</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {!collapsed && (
          <p className="font-display text-[10px] italic text-muted-foreground/60 text-center py-1.5">
            v{__APP_VERSION__}
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
